import React, { useRef, useEffect, useState } from 'react';
import './hero.scss';
import Player from '@vimeo/player';
import unmuteIcon from '../../assets/muted.svg';
import muteIcon from '../../assets/unmuted.svg';
import logo from '../../assets/logo.png';
import gsap from 'gsap';

function Hero() {
  const vimeoSRef = useRef();
  const vimeoLRef = useRef();
  const playerSRef = useRef();
  const playerLRef = useRef();
  const mutedIconRef = useRef();
  const unmutedIconRef = useRef();
  const scrollIconRef = useRef();
  const heroRef = useRef();
  const [soundOn, setSoundOn] = useState(false);
  const [vimeoId, setVimeoId] = useState(null);
  const [currentVimeo, setCurrentVimeo] = useState(null);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState(0);

  const sendClickEvent = (button) => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'click_button', event_name: button });
    }
  };

  const handleMute = (player) => {
    if (!soundOn) {
      player
        .setMuted(false)
        .then(() => {
          player.setCurrentTime(0);
          player.setVolume(0.5);
          setSoundOn(true);
          mutedIconRef.current.style.animation = 'fade-out 0.5s linear forwards';
          unmutedIconRef.current.style.animation = 'fade-in 0.5s linear 0.5s forwards';
          sendClickEvent('unmute');
        })
        .then(player.play())
        .catch(function (error) {
          console.log(error);
        });
    } else {
      player
        .setMuted(true)
        .then(() => {
          setSoundOn(false);
          unmutedIconRef.current.style.animation = 'fade-out 0.5s linear forwards';
          mutedIconRef.current.style.animation = 'fade-in 0.5s linear 0.5s forwards';
          sendClickEvent('mute');
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  useEffect(() => {
    const tl = gsap.timeline();
    tl.to(scrollIconRef.current, {
      opacity: 1,
      scale: 0.9,
      y: '-10px',
      duration: 0.5,
      delay: 1.5
    })
      .to(scrollIconRef.current, { scale: 1, y: 0, duration: 0.2 })
      .to(scrollIconRef.current, { scale: 0.5, y: '200px', opacity: 0, duration: 0.7 }, '>5');
  }, []);

  useEffect(() => {
    playerSRef.current = new Player(vimeoSRef.current, {
      id: 875318938,
      loop: true,
      controls: false,
      responsive: true
    });
    playerLRef.current = new Player(vimeoLRef.current, {
      id: 875318800,
      loop: true,
      controls: false,
      responsive: true
    });
  }, []);

  useEffect(() => {
    if (currentVimeo) {
      currentVimeo.current
        .loadVideo(vimeoId)
        .then(() => {
          setLoaded(true);
          currentVimeo.current.setMuted(true);
        })
        .then(() => {
          setSoundOn(false);
          unmutedIconRef.current.style.animation = 'fade-out 0.5s linear forwards';
          mutedIconRef.current.style.animation = 'fade-in 0.5s linear 0.5s forwards';
        })
        .then(() => {
          currentVimeo.current.play();
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [currentVimeo, vimeoId]);

  useEffect(() => {
    const handleResize = () => {
      const current = window.innerWidth;
      if (current > 767) {
        setCurrentVimeo(playerLRef);
        setVimeoId(875318800);
      } else {
        setCurrentVimeo(playerSRef);
        setVimeoId(875318938);
      }
    };
    window.addEventListener('resize', handleResize, { passive: true });
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach((entry) => {
          if (currentVimeo & loaded) {
            if (heroRef.current) {
              if (!entry.isIntersecting) {
                currentVimeo.current
                  .pause()
                  .then(() => {
                    setPaused(true);
                  })
                  .catch((error) => {
                    console.log(error);
                  });
              } else {
                if (paused) {
                  currentVimeo.current
                    .play()
                    .then(() => {
                      setPaused(false);
                    })
                    .catch((error) => {
                      console.log(error);
                    });
                }
              }
            }
          }
        });
      },
      { threshold: [0] }
    );
    observer.observe(heroRef.current);
  }, [heroRef, currentVimeo, paused, loaded]);

  return (
    <section className="hero" ref={heroRef}>
      <div className="hero__video">
        <div ref={vimeoLRef} className="hero__video__player hero__video__player--l" />
        <div ref={vimeoSRef} className="hero__video__player hero__video__player--s" />

        <a
          href="https://sponsorcontent.cnn.com/interactive/intel/it-starts-with-intel/"
          target="_blank"
          rel="sponsored"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="14"
            viewBox="0 0 8 14"
            fill="none"
          >
            <path
              className="arrow__path"
              d="M0.363604 6.3636C0.0121321 6.71508 0.012132 7.28492 0.363604 7.6364L6.09117 13.364C6.44264 13.7154 7.01249 13.7154 7.36396 13.364C7.71543 13.0125 7.71543 12.4426 7.36396 12.0912L2.27279 7L7.36396 1.90883C7.71543 1.55736 7.71543 0.987512 7.36396 0.63604C7.01249 0.284568 6.44264 0.284568 6.09117 0.63604L0.363604 6.3636ZM2 6.1L1 6.1L1 7.9L2 7.9L2 6.1Z"
              fill="white"
            />
          </svg>
          Back to hub
        </a>

        <button
          ref={mutedIconRef}
          className="icon icon-sound icon-sound-muted"
          onClick={() => {
            handleMute(currentVimeo.current);
          }}
          aria-label="Unmute"
        >
          <img src={unmuteIcon} alt="" />
        </button>
        <button
          ref={unmutedIconRef}
          className="icon icon-sound icon-sound-unmuted"
          onClick={() => {
            handleMute(currentVimeo.current);
          }}
          aria-label="Mute"
        >
          <img src={muteIcon} alt="" />
        </button>
        <svg
          width="78"
          height="83"
          viewBox="0 0 78 83"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="icon icon-scroll"
          ref={scrollIconRef}
        >
          <path
            d="M30.9375 32.4766C30.9375 35.3228 28.6275 37.6328 25.7813 37.6328C22.935 37.6328 20.625 35.3228 20.625 32.4766V24.7422C20.625 21.8959 22.935 19.5859 25.7813 19.5859C28.6275 19.5859 30.9375 21.8959 30.9375 24.7422V32.4766Z"
            fill="white"
          />
          <path
            d="M77.3437 54.2076L73.7292 50.5234L70.8984 53.4058V19.5859H65.7422V53.4058L62.9114 50.5234L59.2969 54.2076L64.7058 59.7145L64.6903 59.7299L68.3048 63.4141L68.3203 63.3986L68.3358 63.4141L71.9503 59.7299L71.9348 59.7145L77.3437 54.2076Z"
            fill="white"
            className="icon-scroll__arrow"
          />
          <path
            d="M25.7812 5.40625C37.1534 5.40625 46.4062 14.6591 46.4062 26.0312V56.9688C46.4062 68.3409 37.1534 77.5938 25.7812 77.5938C14.4091 77.5938 5.15625 68.3409 5.15625 56.9688V26.0312C5.15625 14.6591 14.4091 5.40625 25.7812 5.40625ZM25.7812 0.25C11.5423 0.25 0 11.7923 0 26.0312V56.9688C0 71.2052 11.5423 82.75 25.7812 82.75C40.0202 82.75 51.5625 71.2052 51.5625 56.9688V26.0312C51.5625 11.7923 40.0202 0.25 25.7812 0.25Z"
            fill="white"
          />
        </svg>
      </div>
      <div className="hero__text">
        <img alt="It starts with Intel Logo" src={logo} className="logo" />
        <h1>AI With Purpose</h1>
        <h2>Four Ways AI is Improving the World</h2>
      </div>
    </section>
  );
}

export default Hero;
